/**
 * Recherche d'adresses belges via BeSt Address (SPF BOSA) : registre officiel,
 * accès ouvert sans clé ni inscription (« gratis maar zonder garanties » — pas
 * d'engagement de disponibilité, mais aucun secret à stocker côté client).
 * CORS ouvert : appelé directement depuis le navigateur, sans passer par notre
 * propre API.
 *
 * Couvre les trois régions (Wallonie, Flandre, Bruxelles) — vérifié
 * spécifiquement sur Bastogne, siège de l'unité.
 */

const BASE_URL = 'https://best.pr.fedservices.be/api/opendata/best/v1/belgianAddress/v2/addresses';

export interface AddressSuggestion {
  id: string;
  /** Adresse formatée, prête à stocker telle quelle : « Rue X 12, 6600 Ville ». */
  label: string;
}

/**
 * Sépare la saisie en morceaux exploitables par l'API, qui n'a pas de
 * paramètre de recherche libre unique.
 *
 * Calé sur la convention déjà en usage dans l'application (voir les adresses
 * de démonstration dans `seed.ts`) : « rue et numéro, code postal ville ».
 * Tant que la virgule n'est pas tapée, seuls la rue (et le numéro s'il est
 * déjà là) servent à filtrer — la recherche reste utile dès la première
 * lettre de la rue.
 */
function parseAddressQuery(input: string): URLSearchParams {
  const commaIndex = input.lastIndexOf(',');
  const left = (commaIndex >= 0 ? input.slice(0, commaIndex) : input).trim();
  const right = commaIndex >= 0 ? input.slice(commaIndex + 1).trim() : '';

  const params = new URLSearchParams();

  const streetMatch = /^(.*?)\s+(\d+[a-zA-Z]*)$/.exec(left);
  const streetName = (streetMatch ? streetMatch[1]! : left).trim();
  if (streetName) params.set('streetName', `*${streetName}*`);
  if (streetMatch) params.set('houseNumber', streetMatch[2]!);

  if (right) {
    const cityMatch = /^(\d{4})?\s*(.*)$/.exec(right);
    const postCode = cityMatch?.[1];
    const municipalityName = cityMatch?.[2]?.trim();
    if (postCode) params.set('postCode', postCode);
    if (municipalityName) params.set('municipalityName', `*${municipalityName}*`);
  }

  return params;
}

/** Première valeur définie, dans l'ordre français puis néerlandais puis allemand. */
function pickLanguage(names: Partial<Record<'fr' | 'nl' | 'de', string>> | undefined): string {
  return names?.fr ?? names?.nl ?? names?.de ?? '';
}

interface RawAddressItem {
  id: string;
  houseNumber: string;
  boxNumber?: string | null;
  hasStreetName?: { name?: Partial<Record<'fr' | 'nl' | 'de', string>> };
  hasMunicipality?: { name?: Partial<Record<'fr' | 'nl' | 'de', string>> };
  hasPostalInfo?: { postCode?: string };
}

function toSuggestion(item: RawAddressItem): AddressSuggestion {
  const street = pickLanguage(item.hasStreetName?.name);
  const municipality = pickLanguage(item.hasMunicipality?.name);
  const postCode = item.hasPostalInfo?.postCode ?? '';
  const box = item.boxNumber ? ` boîte ${item.boxNumber}` : '';
  const streetLine = [street, item.houseNumber].filter(Boolean).join(' ') + box;
  const cityLine = [postCode, municipality].filter(Boolean).join(' ');
  return { id: item.id, label: [streetLine, cityLine].filter(Boolean).join(', ') };
}

/**
 * Recherche des adresses. Renvoie un tableau vide sous la barre des trois
 * caractères (requêtes trop larges, coûteuses et peu utiles) ou si le service
 * répond mal — l'appelant garde alors la saisie libre, sans blocage.
 */
export async function searchAddresses(query: string, signal?: AbortSignal): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];

  const params = parseAddressQuery(trimmed);
  if (!params.has('streetName') && !params.has('municipalityName')) return [];

  const response = await fetch(`${BASE_URL}?${params.toString()}`, {
    headers: {
      Accept: 'application/json',
      // Identifiant d'audit exigé par le service — un par requête suffit,
      // aucune valeur secrète n'est nécessaire.
      'BelGov-Trace-Id': `bala-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    },
    signal,
  });
  if (!response.ok) throw new Error(`Recherche d'adresse indisponible (${response.status}).`);

  const data = (await response.json()) as { items?: RawAddressItem[] };
  return (data.items ?? []).slice(0, 10).map(toSuggestion);
}
